import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, Loader2, Dumbbell, Utensils, User, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { fetchApi } from '../lib/utils';

// Onboarding steps - detailed customization
const STEPS = [
  { id: 'personal', title: 'Personal Details', icon: User },
  { id: 'training', title: 'Training Preferences', icon: Dumbbell },
  { id: 'nutrition', title: 'Food Preferences', icon: Utensils },
];

const EXPERIENCE_LEVELS = [
  { id: 'beginner', label: 'Beginner', desc: '0-6 months of training' },
  { id: 'intermediate', label: 'Intermediate', desc: '6 months - 2 years' },
  { id: 'advanced', label: 'Advanced', desc: '2+ years consistent training' },
];

const DIETARY_PREFERENCES = [
  { id: 'no_preference', label: 'No Preference' },
  { id: 'vegetarian', label: 'Vegetarian' },
  { id: 'vegan', label: 'Vegan' },
  { id: 'pescatarian', label: 'Pescatarian' },
  { id: 'halal', label: 'Halal' },
  { id: 'kosher', label: 'Kosher' },
  { id: 'gluten_free', label: 'Gluten Free' },
  { id: 'dairy_free', label: 'Dairy Free' },
  { id: 'keto', label: 'Keto / Low Carb' },
];

const CUISINES = [
  { id: 'western', label: 'Western' },
  { id: 'asian', label: 'Asian' },
  { id: 'mediterranean', label: 'Mediterranean' },
  { id: 'indian', label: 'Indian' },
  { id: 'middle_eastern', label: 'Middle Eastern' },
  { id: 'african', label: 'African' },
  { id: 'latin', label: 'Latin American' },
];

export default function Onboarding() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isChallenge = searchParams.get('challenge') === 'true';
  const { updateUser } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [generatingProgram, setGeneratingProgram] = useState(false);

  const [formData, setFormData] = useState({
    // Personal
    date_of_birth: '',
    gender: '',
    // Training
    experience_level: '',
    days_per_week: '3',
    time_per_session: '45',
    training_location: 'gym',
    // Nutrition
    dietary_preference: 'no_preference',
    preferred_cuisine: '',
    allergies: '',
  });

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const isStepValid = () => {
    switch (STEPS[currentStep].id) {
      case 'personal':
        return formData.date_of_birth !== '';
      case 'training':
        return formData.experience_level !== '';
      case 'nutrition':
        return true;
      default:
        return true;
    }
  };

  const handleNext = async () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      await completeOnboarding();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const completeOnboarding = async () => {
    setLoading(true);
    try {
      // Save all onboarding data
      await fetchApi('/onboarding/update', {
        method: 'PUT',
        body: JSON.stringify({
          body_metrics: {
            gender: formData.gender,
          },
          training: {
            experience_level: formData.experience_level,
            days_per_week: parseInt(formData.days_per_week),
            time_per_session: parseInt(formData.time_per_session),
          },
          environment: {
            training_location: formData.training_location,
          },
          nutrition: {
            dietary_preference: formData.dietary_preference,
            preferred_cuisines: formData.preferred_cuisine ? [formData.preferred_cuisine] : [],
            allergies: formData.allergies ? formData.allergies.split(',').map(a => a.trim()) : [],
          },
          date_of_birth: formData.date_of_birth,
        }),
      });

      // Generate initial program
      setGeneratingProgram(true);
      await fetchApi('/programs/generate', { method: 'POST' });
      await fetchApi('/nutrition/generate', { method: 'POST' });

      // Update user status
      updateUser({ onboarding_completed: true });
      
      // Navigate to subscription
      navigate(isChallenge ? '/subscription?challenge=true' : '/subscription');
    } catch (error) {
      console.error('Onboarding error:', error);
    } finally {
      setLoading(false);
      setGeneratingProgram(false);
    }
  };

  const progress = ((currentStep + 1) / STEPS.length) * 100;

  // Generating program screen
  if (generatingProgram) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="w-20 h-20 rounded-full bg-[#CCFF00]/20 flex items-center justify-center mx-auto mb-6">
            <Sparkles className="w-10 h-10 text-[#CCFF00] animate-pulse" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Creating Your Personalised Plan</h2>
          <p className="text-zinc-400 mb-6">This will just take a moment...</p>
          <Loader2 className="w-8 h-8 animate-spin text-[#CCFF00] mx-auto" />
        </motion.div>
      </div>
    );
  }

  const renderStepContent = () => {
    const step = STEPS[currentStep].id;

    switch (step) {
      case 'personal':
        return (
          <div className="space-y-6">
            <div>
              <Label className="text-zinc-300">Date of Birth <span className="text-red-500">*</span></Label>
              <Input
                type="date"
                value={formData.date_of_birth}
                onChange={(e) => updateFormData('date_of_birth', e.target.value)}
                className="mt-2 bg-zinc-900 border-zinc-800 h-12"
                data-testid="dob-input"
              />
              <p className="text-xs text-zinc-500 mt-2">We use this to personalise your program</p>
            </div>
            <div>
              <Label className="text-zinc-300">Gender</Label>
              <Select value={formData.gender} onValueChange={(val) => updateFormData('gender', val)}>
                <SelectTrigger className="mt-2 bg-zinc-900 border-zinc-800 h-12" data-testid="gender-select">
                  <SelectValue placeholder="Select gender (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                  <SelectItem value="other">Prefer not to say</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        );

      case 'training':
        return (
          <div className="space-y-6">
            <div>
              <Label className="text-zinc-300 mb-3 block">Experience Level <span className="text-red-500">*</span></Label>
              <div className="space-y-3">
                {EXPERIENCE_LEVELS.map((level) => (
                  <button
                    key={level.id}
                    onClick={() => updateFormData('experience_level', level.id)}
                    className={`w-full p-4 rounded-xl text-left transition-all ${
                      formData.experience_level === level.id
                        ? 'bg-[#CCFF00]/20 border-2 border-[#CCFF00]'
                        : 'bg-zinc-900 border-2 border-zinc-800 hover:border-zinc-700'
                    }`}
                    data-testid={`experience-${level.id}`}
                  >
                    <h4 className="font-bold">{level.label}</h4>
                    <p className="text-sm text-zinc-400">{level.desc}</p>
                  </button>
                ))}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-300">Days per Week</Label>
                <Select value={formData.days_per_week} onValueChange={(val) => updateFormData('days_per_week', val)}>
                  <SelectTrigger className="mt-2 bg-zinc-900 border-zinc-800 h-12" data-testid="days-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[2, 3, 4, 5, 6].map(d => (
                      <SelectItem key={d} value={String(d)}>{d} days</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-300">Session Duration</Label>
                <Select value={formData.time_per_session} onValueChange={(val) => updateFormData('time_per_session', val)}>
                  <SelectTrigger className="mt-2 bg-zinc-900 border-zinc-800 h-12" data-testid="time-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="30">30 min</SelectItem>
                    <SelectItem value="45">45 min</SelectItem>
                    <SelectItem value="60">60 min</SelectItem>
                    <SelectItem value="90">90 min</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label className="text-zinc-300">Where will you train?</Label>
              <div className="grid grid-cols-3 gap-3 mt-2">
                {[
                  { id: 'gym', label: 'Gym' },
                  { id: 'home', label: 'Home' },
                  { id: 'outdoors', label: 'Outdoors' },
                ].map((loc) => (
                  <button
                    key={loc.id}
                    onClick={() => updateFormData('training_location', loc.id)}
                    className={`p-3 rounded-xl text-center transition-all ${
                      formData.training_location === loc.id
                        ? 'bg-[#CCFF00]/20 border-2 border-[#CCFF00]'
                        : 'bg-zinc-900 border-2 border-zinc-800 hover:border-zinc-700'
                    }`}
                    data-testid={`location-${loc.id}`}
                  >
                    {loc.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      case 'nutrition':
        return (
          <div className="space-y-6">
            <div>
              <Label className="text-zinc-300">Dietary Preference</Label>
              <Select value={formData.dietary_preference} onValueChange={(val) => updateFormData('dietary_preference', val)}>
                <SelectTrigger className="mt-2 bg-zinc-900 border-zinc-800 h-12" data-testid="diet-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DIETARY_PREFERENCES.map(d => (
                    <SelectItem key={d.id} value={d.id}>{d.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-zinc-300">Preferred Cuisine</Label>
              <Select value={formData.preferred_cuisine} onValueChange={(val) => updateFormData('preferred_cuisine', val)}>
                <SelectTrigger className="mt-2 bg-zinc-900 border-zinc-800 h-12" data-testid="cuisine-select">
                  <SelectValue placeholder="Select your preferred cuisine" />
                </SelectTrigger>
                <SelectContent>
                  {CUISINES.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-zinc-500 mt-2">We'll create meal plans based on your cultural preferences</p>
            </div>
            
            <div>
              <Label className="text-zinc-300">Allergies or Intolerances</Label>
              <Input
                type="text"
                value={formData.allergies}
                onChange={(e) => updateFormData('allergies', e.target.value)}
                placeholder="e.g., nuts, shellfish, lactose"
                className="mt-2 bg-zinc-900 border-zinc-800 h-12"
                data-testid="allergies-input"
              />
              <p className="text-xs text-zinc-500 mt-2">Separate multiple items with commas</p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-black py-8 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => currentStep > 0 ? handleBack() : navigate('/')}
            className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors mb-6"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>{currentStep > 0 ? 'Back' : 'Exit'}</span>
          </button>

          <div className="flex items-center gap-2 mb-6">
            <div className="w-10 h-10 rounded-xl bg-[#CCFF00] flex items-center justify-center">
              <span className="text-black font-black text-xl">V</span>
            </div>
            <span className="font-bold text-xl">VYTA</span>
          </div>

          {/* Progress */}
          <div className="mb-2">
            <div className="flex justify-between text-sm text-zinc-400 mb-2">
              <span>Step {currentStep + 1} of {STEPS.length}</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-[#CCFF00]"
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>
          </div>
        </div>

        {/* Step Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex items-center gap-3 mb-6">
              {React.createElement(STEPS[currentStep].icon, { className: "w-6 h-6 text-[#CCFF00]" })}
              <h1 className="text-2xl font-bold">{STEPS[currentStep].title}</h1>
            </div>

            {renderStepContent()}
          </motion.div>
        </AnimatePresence>

        {/* Navigation */}
        <div className="mt-8 flex gap-4">
          {currentStep > 0 && (
            <Button
              onClick={handleBack}
              variant="outline"
              className="flex-1 border-zinc-700 text-white hover:bg-zinc-800 h-12"
            >
              Back
            </Button>
          )}
          <Button
            onClick={handleNext}
            disabled={!isStepValid() || loading}
            className={`${currentStep > 0 ? 'flex-1' : 'w-full'} bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold h-12`}
            data-testid="next-btn"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : currentStep === STEPS.length - 1 ? (
              <>Generate My Plan <Sparkles className="w-4 h-4 ml-2" /></>
            ) : (
              <>Continue <ArrowRight className="w-4 h-4 ml-2" /></>
            )}
          </Button>
        </div>

        {/* Skip option for nutrition */}
        {STEPS[currentStep].id === 'nutrition' && (
          <button
            onClick={handleNext}
            className="w-full text-center text-zinc-500 hover:text-zinc-300 text-sm mt-4"
          >
            Skip for now — I'll set this up later
          </button>
        )}
      </div>
    </div>
  );
}
